/**
 * How an activity reads and where it sits — decided once, for every surface.
 *
 * A DELIBERATE COPY of scale-portal's
 * src/components/template/MediatorActivities/activityDisplay.js. The two
 * portals must say the same thing about the same record — "NA" in one and
 * "All day" in the other is the kind of drift that makes them look like two
 * products. The two apps share no package, so this is kept in step by hand;
 * change one and change the other.
 *
 * The board, the Activities list, the tracker log and the form all show a time
 * and all sort by one. Each had its own copy of "All day" and its own ordering,
 * which is how the three ended up disagreeing about where an untimed activity
 * belongs. They import from here instead.
 */

/**
 * THE COLOUR.
 *
 * It must stay distinct from all four availability states, which already use
 * green (available), grey (not available), blue (booked) and amber (on hold).
 * An activity is NOT an availability state — it neither opens nor closes a slot
 * and never blocks a booking — so it cannot borrow any of those or a mediator
 * will read it as one.
 *
 * Lives here rather than in ActivityChip because the hover card needs it too,
 * and importing it from the chip that imports the card is a cycle.
 *
 * OPEN with Adam (10 Oct): he asked "do you think that this purple is the right
 * color?" and Danish said it reads as unimportant and suggested a lighter navy.
 * Adam pushed back — blue already means booked. No replacement was agreed, so
 * the violet stands until one is.
 */
export const ACTIVITY_COLOR = {
    bg: '#f5f3ff', // violet-50
    fg: '#6d28d9', // violet-700 — the title
    time: '#8b5cf6', // violet-500 — the time, one step back like the slot pills
    dot: '#7c3aed', // violet-600 — legend swatch only
    label: 'Activity',
}

/**
 * The words for an activity with no time on it.
 *
 * Adam, 10 Oct: "instead of saying all day, it's going to say like not
 * specified or NA — I think NA is probably just the easiest way to do it", then
 * "All day will go to NA and NA will be the default."
 *
 * "All day" was wrong in a way that mattered: on a calendar it means an event
 * that OCCUPIES the whole day, and these do not. A follow-up call with no fixed
 * time blocks nothing. NA says the time is unknown, which is what is true, and
 * it is what the majority of activities will carry — Judicate West's entries
 * mostly have no time slot either.
 */
export const NO_TIME_LABEL = 'NA'

/* The ordering rank an untimed activity gets. Below every real clock time, so
 * NA sorts to the TOP of the day — Adam: "if something saves as NA, this will
 * go to the top, will be the top thing here". */
const NO_TIME_RANK = -1

/* Where a slot with no time of its own sits, by its half of the day. Only used
 * as a fallback: availability rows normally carry a real slot_time, and without
 * this a timeless row would sort to one end of the day and move a tile that has
 * been in the same place for months. */
const SLOT_FALLBACK_MINUTES = { morning: 9 * 60, fullday: 0, afternoon: 13 * 60 }

/**
 * A time → minutes past midnight, or null when it is not one.
 *
 * Accepts BOTH shapes this codebase stores, because the two boards disagree:
 * scale-portal keeps availability times as 24-hour "HH:MM[:SS]", while the
 * case-manager board has already run them through formatTimeFromHHMM and holds
 * "8:00 AM". Parsing only the first silently returned null for every
 * case-manager slot, which dropped them onto a half-of-day fallback and let an
 * 8:00 AM activity sort above an 8:00 AM booking.
 */
export function minutesOfTime(value) {
    if (!value) return null
    const raw = String(value).trim()
    const [h, m] = raw.split(':')
    const hours = Number(h)
    /* parseInt, not Number: the minute half of "8:00 AM" is "00 AM", which
     * Number() rejects outright. */
    const mins = parseInt(m, 10)
    if (!Number.isFinite(hours) || !Number.isFinite(mins)) return null

    const meridiem = /\b([AP])M\b/i.exec(raw)
    if (!meridiem) return hours * 60 + mins

    let h12 = hours % 12
    if (meridiem[1].toUpperCase() === 'P') h12 += 12
    return h12 * 60 + mins
}

/** The rank an ACTIVITY sorts at. Untimed floats to the top of the day. */
export function activityRank(activity) {
    if (!activity || activity.is_all_day) return NO_TIME_RANK
    const mins = minutesOfTime(activity.local_time)
    return mins === null ? NO_TIME_RANK : mins
}

/** The rank an availability SLOT sorts at, from its own clock time. */
export function slotRank(extendedProps) {
    const ep = extendedProps || {}
    const mins = minutesOfTime(ep.case_schedule_time || ep.time)
    if (mins !== null) return mins
    return SLOT_FALLBACK_MINUTES[ep.slot] ?? 0
}

/**
 * One day's slots and activities in the order they are drawn.
 *
 * Adam, 10 Oct, on a timed activity sitting under the slots: "this 9am should
 * also go above the 10am, so it should always be like chronological", and then,
 * when told activities would simply pin to the top, "I don't think it should
 * always stay on the top — if this is at 1:30pm then it should show after the
 * 10am."
 *
 * So the two are interleaved on the clock rather than stacked in two blocks.
 * The sort is STABLE (Array.prototype.sort is, since ES2019), which is what
 * keeps two tiles at the same minute in the order they arrived instead of
 * shuffling between renders.
 *
 * @returns {Array<{kind:'slot'|'activity', item:object}>}
 */
export function orderDayItems(slots = [], activities = []) {
    const rows = [
        ...slots.map((s) => ({
            kind: 'slot',
            item: s,
            rank: slotRank(s?.extendedProps),
        })),
        ...activities.map((a) => ({
            kind: 'activity',
            item: a,
            rank: activityRank(a),
        })),
    ]
    rows.sort((a, b) => a.rank - b.rank)
    return rows
}

/** 24h "HH:MM" → "9:00 AM". The board's own format, not the browser's. */
export function toAmPm(hhmm) {
    const mins = minutesOfTime(hhmm)
    if (mins === null) return null
    const h = Math.floor(mins / 60)
    const m = String(mins % 60).padStart(2, '0')
    const suffix = h >= 12 ? 'PM' : 'AM'
    return `${h % 12 === 0 ? 12 : h % 12}:${m} ${suffix}`
}

/**
 * What goes in the time column of a chip or a row — EMPTY when there is none.
 *
 * It used to read "NA". Adam, 13 Oct: "I don't think you need to show NA here,
 * I think it looks like broken — just exclude the time." Every caller already
 * guards on the empty string. `NO_TIME_LABEL` stays exported for the ordering
 * rules that still reason about untimed activities; it is no longer rendered.
 */
export function activityTimeLabel(activity) {
    if (!activity || activity.is_all_day) return ''
    return toAmPm(activity.local_time) || ''
}

/**
 * The two dates an activity carries, which Adam separated on 13 Oct.
 *
 *   "You should log the date that it was scheduled — this should be the 13th,
 *    not the 29th… it's booked on the 13th. And then you can say scheduled
 *    for October 29th, and if you have a time, scheduled for October 29th at
 *    9am."
 *
 * So the STAMP on a row is when it was logged, and the BODY says when the
 * thing happens — the shape every reminder in this product already uses.
 */
export function bookedOnLabel(activity, { withYear = true } = {}) {
    const iso = activity?.created_at
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(withYear ? { year: 'numeric' } : {}),
    })
}

/** "Scheduled for Oct 29, 2026" / "… at 9:00 AM". No time means no time shown. */
export function scheduledForLabel(activity) {
    const date = usLongDate(activity?.local_date)
    if (!date) return ''
    const time = activityTimeLabel(activity)
    return time ? `Scheduled for ${date} at ${time}` : `Scheduled for ${date}`
}

/** Oldest-logged first — "this is going to be in chronological order". The API
 *  orders by when an activity HAPPENS, a different sequence once something is
 *  logged today for three weeks out. */
export function byBookedDate(a, b) {
    return (
        new Date(a?.created_at || 0) - new Date(b?.created_at || 0) ||
        Number(a?.id || 0) - Number(b?.id || 0)
    )
}

/**
 * US order, which is the order this product's users read dates in.
 * Adam, 10 Oct: "just make this like US dates please."
 * Built from the parts, never `new Date(str)` — a bare YYYY-MM-DD is parsed as
 * UTC and renders as the previous day for anyone west of Greenwich.
 */
export function usDate(localDate) {
    if (!localDate) return ''
    const [y, m, d] = String(localDate).split('-')
    if (!y || !m || !d) return ''
    return `${m}/${d}/${y}`
}

/** The long form, for the Activities list: "Mon, Oct 12, 2026". */
export function usLongDate(localDate) {
    if (!localDate) return ''
    const [y, m, d] = String(localDate).split('-').map(Number)
    if (!y || !m || !d) return ''
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}
